
import { useState, useEffect, useRef } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PlaceResult } from '@/types/place';
import { 
  searchPlaces, 
  searchPlacesFromSaved, 
  fetchNextPage,
  importPlaceAsLead
} from '@/services/placesService';
import { SavedSearch, markSearchAsUsed } from '@/services/savedSearchService';

const SESSION_KEY = 'places_search_session';

interface SessionState {
  searchQuery: string;
  locationQuery: string;
  radius: number;
  useRadius: boolean;
  results: PlaceResult[];
  hasSearched: boolean;
  nextPageToken?: string;
  searchMeta: Record<string, any> | null;
}

function loadSession(): SessionState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SessionState;
  } catch { return null; }
}

function saveSession(state: SessionState) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch { /* quota exceeded – ignore */ }
}

export const usePlacesSearch = (onImportSuccess?: () => void) => {
  const { toast } = useToast();
  const saved = loadSession();

  const [searchQuery, setSearchQuery] = useState(saved?.searchQuery ?? '');
  const [locationQuery, setLocationQuery] = useState(saved?.locationQuery ?? '');
  const [radius, setRadius] = useState(saved?.radius ?? 1000);
  const [useRadius, setUseRadius] = useState(saved?.useRadius ?? true);
  const [results, setResults] = useState<PlaceResult[]>(saved?.results ?? []);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(saved?.hasSearched ?? false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(saved?.nextPageToken);
  const [searchMeta, setSearchMeta] = useState<Record<string, any> | null>(saved?.searchMeta ?? null);
  const [activeSavedSearch, setActiveSavedSearch] = useState<SavedSearch | null>(null);

  // Persist to sessionStorage on relevant state changes
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    saveSession({ searchQuery, locationQuery, radius, useRadius, results, hasSearched, nextPageToken, searchMeta });
  }, [searchQuery, locationQuery, radius, useRadius, results, hasSearched, nextPageToken, searchMeta]);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!searchQuery && !locationQuery) {
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "Please enter a search term or location.",
      });
      return;
    }
    
    setIsSearching(true);
    setHasSearched(true);
    setNextPageToken(undefined);
    setResults([]);
    
    try {
      const response = await searchPlaces(searchQuery, locationQuery, radius, useRadius);
      
      // Success: OK status with or without results
      if (response.status === 'OK') {
        console.log('Search successful:', {
          resultsCount: response.results.length,
          query: searchQuery,
          location: locationQuery
        });
        
        setResults(response.results);
        setSearchMeta(response.meta || null);
        setNextPageToken(response.next_page_token);
        
        // Only show toast if we have results (silent for empty - let UI handle it)
        if (response.results.length > 0) {
          toast({
            title: "Search Complete",
            description: `Found ${response.results.length} business${response.results.length === 1 ? '' : 'es'}.`,
          });
          
          if (activeSavedSearch) {
            await markSearchAsUsed(activeSavedSearch.id, response.results.length);
          }
        }
      } 
      else if (response.status === 'ZERO_RESULTS') {
        console.info('Places search returned ZERO_RESULTS. Showing empty results state.');
        setResults([]);
        setSearchMeta(response.meta || null);
        setNextPageToken(undefined);
        // No toast here; the empty state UI will handle guidance
      }
      // Soft Error: User can retry with different parameters
      else if (['INVALID_REQUEST', 'OVER_QUERY_LIMIT'].includes(response.status)) {
        console.warn('Search parameter issue:', response.status);
        setResults([]);
        toast({
          variant: "default",
          title: "Adjust Search Parameters",
          description: response.user_friendly_message || "Please try different search terms or location.",
        });
      }
      // Hard Error: System issue
      else {
        console.error('Search system error:', response.status, response.error_message);
        setResults([]);
        toast({
          variant: "destructive",
          title: "Search Unavailable",
          description: response.user_friendly_message || "Unable to complete search. Please try again.",
        });
      }
    } catch (error) {
      console.error('Search exception:', error);
      setResults([]);
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "An unexpected error occurred. Please check your connection and try again.",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleLoadMore = async () => {
    if (!nextPageToken) return;
    
    setIsLoadingMore(true);
    
    try {
      const response = await fetchNextPage(nextPageToken, {
        query: searchQuery,
        location: locationQuery,
        radius: radius,
        useRadius: useRadius,
        ...searchMeta
      });
      
      if (response.status === 'OK') {
        setResults(prevResults => [...prevResults, ...response.results]);
        setNextPageToken(response.next_page_token);
        
        if (response.meta) {
          setSearchMeta(response.meta);
        }
        
        if (response.results.length > 0) {
          toast({
            title: "More Results Loaded",
            description: `Loaded ${response.results.length} more businesses.`,
          });
        }
      } 
      else if (response.status === 'ZERO_RESULTS') {
        console.info('Pagination returned ZERO_RESULTS. No more results.');
        setNextPageToken(undefined);
        // No toast; just stop further loads
      }
      else if (['INVALID_REQUEST', 'OVER_QUERY_LIMIT'].includes(response.status)) {
        console.warn('Load more parameter issue:', response.status);
        toast({
          variant: "default",
          title: "Unable to Load More",
          description: response.user_friendly_message || "Please try again in a moment.",
        });
      } else {
        console.error('Error loading more:', response.status, response.error_message);
        toast({
          variant: "destructive",
          title: "Error Loading More",
          description: response.user_friendly_message || "Failed to load more results.",
        });
      }
    } catch (error) {
      console.error('Exception loading more:', error);
      toast({
        variant: "destructive",
        title: "Error Loading More",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLoadSavedSearch = async (savedSearch: SavedSearch) => {
    setSearchQuery(savedSearch.query || '');
    setLocationQuery(savedSearch.location || '');
    setRadius(savedSearch.radius);
    setActiveSavedSearch(savedSearch);
    
    setIsSearching(true);
    setHasSearched(true);
    setResults([]);
    
    try {
      const response = await searchPlacesFromSaved(savedSearch, useRadius);
      
      if (response.status === 'OK') {
        setResults(response.results);
        setSearchMeta(response.meta || null);
        setNextPageToken(response.next_page_token);
        
        if (response.results.length > 0) {
          toast({
            title: "Saved Search Loaded",
            description: `Found ${response.results.length} business${response.results.length === 1 ? '' : 'es'} matching "${savedSearch.name}".`,
          });
          
          await markSearchAsUsed(savedSearch.id, response.results.length);
        }
      } 
      else if (response.status === 'ZERO_RESULTS') {
        console.info('Saved search returned ZERO_RESULTS. Showing empty results state.');
        setResults([]);
        setSearchMeta(response.meta || null);
        setNextPageToken(undefined);
        // No toast; the empty state will guide the user
      }
      else if (['INVALID_REQUEST', 'OVER_QUERY_LIMIT'].includes(response.status)) {
        console.warn('Saved search parameter issue:', response.status);
        setResults([]);
        toast({
          variant: "default",
          title: "Adjust Search",
          description: response.user_friendly_message || "Please update your saved search parameters.",
        });
      } else {
        console.error('Saved search error:', response.status, response.error_message);
        setResults([]);
        toast({
          variant: "destructive",
          title: "Search Error",
          description: response.user_friendly_message || "Failed to run saved search.",
        });
      }
    } catch (error) {
      console.error('Exception running saved search:', error);
      setResults([]);
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleImport = async (place: PlaceResult) => {
    const startTime = Date.now();
    
    try {
      // Pre-check authentication before attempting import
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          variant: "destructive",
          title: "Sign in required",
          description: "Please sign in to import leads.",
        });
        throw new Error('auth_required');
      }
      
      console.log(`[${new Date().toISOString()}] Starting import for: ${place.name}`);
      
      const lead = await importPlaceAsLead(place, searchQuery);
      
      if (lead) {
        const duration = Date.now() - startTime;
        console.log(`[${new Date().toISOString()}] Import completed in ${duration}ms - Lead ID: ${lead.id}`);
        
        // Success notification is handled in importService
        if (onImportSuccess) {
          onImportSuccess();
        }
        
        return lead;
      } else {
        throw new Error('Import returned null');
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`[${new Date().toISOString()}] Import failed after ${duration}ms:`, error);
      
      // Error notification is handled in importService
      // Just rethrow to propagate to UI
      throw error;
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setLocationQuery('');
    setResults([]);
    setHasSearched(false);
    setSearchMeta(null);
    setActiveSavedSearch(null);
    setNextPageToken(undefined);
    sessionStorage.removeItem(SESSION_KEY);
  };

  return {
    searchQuery,
    setSearchQuery,
    locationQuery,
    setLocationQuery,
    radius,
    setRadius,
    useRadius,
    setUseRadius,
    results,
    isSearching,
    isLoadingMore,
    hasSearched,
    nextPageToken,
    searchMeta,
    activeSavedSearch,
    handleSearch,
    handleLoadMore,
    handleLoadSavedSearch,
    handleImport,
    clearSearch
  };
};
